import React, { useState, useRef, useCallback } from 'react';
import { Layout } from '@/components/layout';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import {
  Package, Plus, Trash2, Play, ArrowLeft, CheckCircle2,
  XCircle, Loader2, RefreshCw, Store, Filter, Settings2, Eye, SkipForward, ChevronsUpDown, Check,
  MapPin, AlertTriangle,
} from 'lucide-react';
import {
  Select, SelectContent, SelectGroup, SelectItem,
  SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ────────────────────────────────────────────────────────────────────

type UpdateRuleType =
  | 'set_title' | 'find_replace_title'
  | 'set_vendor' | 'set_product_type'
  | 'add_tag' | 'remove_tag' | 'set_tags'
  | 'set_status'
  | 'set_body_html' | 'find_replace_body_html'
  | 'set_price' | 'increase_price_pct' | 'decrease_price_pct'
  | 'increase_price_amt' | 'decrease_price_amt'
  | 'set_compare_at_price' | 'clear_compare_at_price' | 'set_price_to_compare_at'
  | 'set_sku' | 'find_replace_sku'
  | 'set_weight' | 'set_taxable' | 'set_inventory_policy' | 'set_track_quantity'
  | 'set_template'
  | 'prepend_title' | 'append_title'
  | 'set_metafield'
  | 'convert_description_to_paragraphs'
  | 'remove_description_between'
  | 'assign_inventory_location'
  | 'remove_inventory_location'
  | 'set_inventory_quantity';

interface FilterRule { type: string; operator: string; value?: string }
interface UpdateRule {
  type: UpdateRuleType;
  value?: string;
  find?: string;
  replace?: string;
  namespace?: string;
  metaKey?: string;
  metaType?: string;
  locationId?: string;
}
interface FieldChange { field: string; oldValue: string; newValue: string }
interface PreviewItem { productId: number; title: string; status: string; vendor: string; image: string | null; changes: FieldChange[] }
interface PreviewResult { totalScanned: number; totalMatched: number; items: PreviewItem[] }

interface RunEvent {
  type: 'start' | 'fetched' | 'product' | 'done' | 'error' | 'progress';
  productId?: number; title?: string; success?: boolean; error?: string; changes?: FieldChange[];
  totalScanned?: number; totalUpdated?: number; totalSkipped?: number; totalErrors?: number;
  processed?: number; total?: number;
  message?: string;
}

interface RunProgress {
  processed: number;
  total: number;
  totalUpdated: number;
  totalSkipped: number;
  totalErrors: number;
}

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

// ── Remove Location types ─────────────────────────────────────────────────────

interface LocationVariant { variantId: number; inventoryItemId: number; sku: string; qty: number }
interface LocationProduct { id: number; title: string; vendor: string; image: string | null; totalQty: number; variants: LocationVariant[] }
interface RemoveProductEvent { productId: number; title: string; success: boolean; error?: string; processed: number; total: number }

// ── Filter config ─────────────────────────────────────────────────────────────

type ValueType = 'none' | 'text' | 'number' | 'date' | 'select' | 'id_list';
interface OperatorConfig { value: string; label: string; valueType: ValueType; selectOpts?: { value: string; label: string }[] }
interface FilterTypeDef  { value: string; label: string; group: string; operators: OperatorConfig[] }

// Reusable operator sets
const TEXT_OPS: OperatorConfig[] = [
  { value: 'contains',     label: 'contains',         valueType: 'text' },
  { value: 'not_contains', label: 'does not contain', valueType: 'text' },
  { value: 'equals',       label: 'equals',           valueType: 'text' },
  { value: 'not_equals',   label: 'does not equal',   valueType: 'text' },
  { value: 'starts_with',  label: 'starts with',      valueType: 'text' },
  { value: 'ends_with',    label: 'ends with',        valueType: 'text' },
  { value: 'is_blank',     label: 'is blank',         valueType: 'none' },
  { value: 'is_not_blank', label: 'is not blank',     valueType: 'none' },
];
const TEXT_SHORT: OperatorConfig[] = TEXT_OPS.filter(o =>
  ['contains','not_contains','equals','not_equals','is_blank','is_not_blank'].includes(o.value));
const NUM_OPS: OperatorConfig[] = [
  { value: 'equals',       label: '= equals',       valueType: 'number' },
  { value: 'not_equals',   label: '≠ not equals',   valueType: 'number' },
  { value: 'greater_than', label: '> greater than', valueType: 'number' },
  { value: 'less_than',    label: '< less than',    valueType: 'number' },
  { value: 'gte',          label: '≥ at least',     valueType: 'number' },
  { value: 'lte',          label: '≤ at most',      valueType: 'number' },
];
const NUM_BLANK_OPS: OperatorConfig[] = [
  ...NUM_OPS,
  { value: 'is_blank',     label: 'is blank',     valueType: 'none' },
  { value: 'is_not_blank', label: 'is not blank', valueType: 'none' },
];
const DATE_OPS: OperatorConfig[] = [
  { value: 'before',       label: 'before',          valueType: 'date' },
  { value: 'after',        label: 'after',           valueType: 'date' },
  { value: 'last_7_days',  label: 'in last 7 days',  valueType: 'none' },
  { value: 'last_14_days', label: 'in last 14 days', valueType: 'none' },
  { value: 'last_30_days', label: 'in last 30 days', valueType: 'none' },
  { value: 'last_90_days', label: 'in last 90 days', valueType: 'none' },
  { value: 'last_year',    label: 'in last year',    valueType: 'none' },
];
const DATE_BLANK_OPS: OperatorConfig[] = [
  ...DATE_OPS,
  { value: 'is_blank',     label: 'is not set', valueType: 'none' },
  { value: 'is_not_blank', label: 'is set',     valueType: 'none' },
];
const BOOL_OPS: OperatorConfig[] = [
  { value: 'true',  label: 'Yes', valueType: 'none' },
  { value: 'false', label: 'No',  valueType: 'none' },
];
const STATUS_OPTS = [
  { value: 'active', label: 'Active' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' },
];

const FILTER_TYPE_DEFS: FilterTypeDef[] = [
  // ── Product Fields ──────────────────────────────────────────────────────────
  { value: 'title',           label: 'Title',                    group: 'Product Fields',     operators: TEXT_OPS },
  { value: 'description',     label: 'Description',              group: 'Product Fields',     operators: TEXT_SHORT },
  { value: 'handle',          label: 'Handle (URL)',             group: 'Product Fields',     operators: TEXT_OPS.filter(o => !['starts_with','ends_with'].includes(o.value)) },
  { value: 'vendor',          label: 'Vendor',                   group: 'Product Fields',     operators: TEXT_OPS },
  { value: 'product_type',    label: 'Product Type',             group: 'Product Fields',     operators: TEXT_OPS },
  { value: 'status',          label: 'Status',                   group: 'Product Fields',     operators: [
    { value: 'is',     label: 'is',     valueType: 'select', selectOpts: STATUS_OPTS },
    { value: 'is_not', label: 'is not', valueType: 'select', selectOpts: STATUS_OPTS },
  ]},
  { value: 'tag',             label: 'Tag',                      group: 'Product Fields',     operators: [
    { value: 'includes',     label: 'includes (exact)',   valueType: 'text' },
    { value: 'not_includes', label: 'does not include',  valueType: 'text' },
    { value: 'contains',     label: 'contains text',     valueType: 'text' },
    { value: 'is_blank',     label: 'has no tags',       valueType: 'none' },
    { value: 'is_not_blank', label: 'has tags',          valueType: 'none' },
  ]},
  { value: 'product_id',      label: 'Product ID',               group: 'Product Fields',     operators: [
    { value: 'equals',  label: 'equals',              valueType: 'text' },
    { value: 'in_list', label: 'is in list (csv)',     valueType: 'id_list' },
  ]},
  { value: 'created_at',      label: 'Date Created',             group: 'Product Fields',     operators: DATE_OPS },
  { value: 'updated_at',      label: 'Date Updated',             group: 'Product Fields',     operators: DATE_OPS },
  { value: 'published_at',    label: 'Date Published',           group: 'Product Fields',     operators: DATE_BLANK_OPS },
  { value: 'inventory_quantity', label: 'Inventory Quantity',    group: 'Product Fields',     operators: NUM_OPS },
  { value: 'variant_count',   label: 'Variant Count',            group: 'Product Fields',     operators: NUM_OPS },
  { value: 'option1_name',    label: 'Option 1 Name',            group: 'Product Fields',     operators: TEXT_OPS },
  { value: 'option2_name',    label: 'Option 2 Name',            group: 'Product Fields',     operators: TEXT_OPS },
  { value: 'option3_name',    label: 'Option 3 Name',            group: 'Product Fields',     operators: TEXT_OPS },
  { value: 'template_suffix', label: 'Theme Template',           group: 'Product Fields',     operators: [
    { value: 'equals',       label: 'equals',             valueType: 'text' },
    { value: 'contains',     label: 'contains',           valueType: 'text' },
    { value: 'is_blank',     label: 'is default',         valueType: 'none' },
    { value: 'is_not_blank', label: 'is custom template', valueType: 'none' },
  ]},
  { value: 'visible_online',  label: 'Visible on Online Store',  group: 'Product Fields',     operators: BOOL_OPS },
  { value: 'visible_pos',     label: 'Visible on POS',           group: 'Product Fields',     operators: BOOL_OPS },

  // ── Variant Fields ──────────────────────────────────────────────────────────
  { value: 'variant_price',            label: 'Price',                          group: 'Variant Fields', operators: NUM_OPS },
  { value: 'variant_compare_at_price', label: 'Compare-at Price',               group: 'Variant Fields', operators: NUM_BLANK_OPS },
  { value: 'variant_sku',              label: 'SKU',                            group: 'Variant Fields', operators: TEXT_SHORT },
  { value: 'variant_barcode',          label: 'Barcode',                        group: 'Variant Fields', operators: TEXT_SHORT },
  { value: 'variant_inventory_quantity', label: 'Variant Inventory Qty',        group: 'Variant Fields', operators: NUM_OPS },
  { value: 'variant_inventory_policy',   label: 'Inventory Out of Stock Policy',group: 'Variant Fields', operators: [
    { value: 'equals', label: 'is', valueType: 'select', selectOpts: [
      { value: 'deny',     label: 'Deny (stop selling)' },
      { value: 'continue', label: 'Continue (keep selling)' },
    ]},
  ]},
  { value: 'variant_taxable',          label: 'Charge Tax',                     group: 'Variant Fields', operators: BOOL_OPS },
  { value: 'variant_physical',         label: 'Physical Product (ships)',        group: 'Variant Fields', operators: BOOL_OPS },
  { value: 'variant_track_quantity',   label: 'Track Quantity',                 group: 'Variant Fields', operators: BOOL_OPS },
  { value: 'variant_title',            label: 'Variant Title',                  group: 'Variant Fields', operators: TEXT_SHORT },
  { value: 'variant_weight',           label: 'Weight',                         group: 'Variant Fields', operators: NUM_OPS },
  { value: 'variant_weight_grams',     label: 'Weight in Grams',                group: 'Variant Fields', operators: NUM_OPS },
  { value: 'variant_weight_unit',      label: 'Weight Unit',                    group: 'Variant Fields', operators: [
    { value: 'equals', label: 'is', valueType: 'select', selectOpts: [
      { value: 'g',  label: 'Grams (g)' }, { value: 'kg', label: 'Kilograms (kg)' },
      { value: 'lb', label: 'Pounds (lb)' }, { value: 'oz', label: 'Ounces (oz)' },
    ]},
  ]},
  { value: 'variant_option1_value', label: 'Option 1 Value', group: 'Variant Fields', operators: TEXT_OPS.filter(o => ['contains','not_contains','equals','not_equals'].includes(o.value)) },
  { value: 'variant_option2_value', label: 'Option 2 Value', group: 'Variant Fields', operators: TEXT_OPS.filter(o => ['contains','not_contains','equals','not_equals'].includes(o.value)) },
  { value: 'variant_option3_value', label: 'Option 3 Value', group: 'Variant Fields', operators: TEXT_OPS.filter(o => ['contains','not_contains','equals','not_equals'].includes(o.value)) },

  // ── Special Conditions ──────────────────────────────────────────────────────
  { value: 'has_images',       label: 'Has Images',                             group: 'Special Conditions', operators: [
    { value: 'true',  label: 'Has images',          valueType: 'none' },
    { value: 'false', label: "Doesn't have images", valueType: 'none' },
  ]},
  { value: 'compare_at_blank', label: 'Compare-at Price is Blank',              group: 'Special Conditions', operators: [
    { value: 'is_blank', label: 'is blank (all variants)', valueType: 'none' },
  ]},
  { value: 'price_vs_compare', label: 'Price vs Compare-at Price',              group: 'Special Conditions', operators: [
    { value: 'less_than',    label: 'Price < Compare-at (on sale)', valueType: 'none' },
    { value: 'equals',       label: 'Price = Compare-at',           valueType: 'none' },
    { value: 'greater_than', label: 'Price > Compare-at',           valueType: 'none' },
  ]},
  { value: 'out_of_stock',     label: 'Product Completely Out of Stock',        group: 'Special Conditions', operators: [
    { value: 'true', label: 'is completely out of stock', valueType: 'none' },
  ]},
  { value: 'duplicate_sku',    label: 'Has Duplicate SKU',                      group: 'Special Conditions', operators: [
    { value: 'true', label: 'has a duplicate SKU', valueType: 'none' },
  ]},
  { value: 'duplicate_barcode',label: 'Has Duplicate Barcode',                  group: 'Special Conditions', operators: [
    { value: 'true', label: 'has a duplicate barcode', valueType: 'none' },
  ]},
  { value: 'duplicate_title',  label: 'Has Duplicate Title',                    group: 'Special Conditions', operators: [
    { value: 'true', label: 'has a duplicate title', valueType: 'none' },
  ]},
];

const FILTER_GROUPS = ['Product Fields', 'Variant Fields', 'Special Conditions'] as const;
const GROUPED_FILTERS = FILTER_GROUPS.reduce((acc, g) => {
  acc[g] = FILTER_TYPE_DEFS.filter((d) => d.group === g);
  return acc;
}, {} as Record<string, FilterTypeDef[]>);

const UPDATE_RULE_GROUPS: { group: string; rules: { value: UpdateRuleType; label: string; inputs: string[] }[] }[] = [
  {
    group: 'Product Fields',
    rules: [
      { value: 'set_title',             label: 'Set title to',             inputs: ['value'] },
      { value: 'prepend_title',         label: 'Prepend to title',         inputs: ['value'] },
      { value: 'append_title',          label: 'Append to title',          inputs: ['value'] },
      { value: 'find_replace_title',    label: 'Find & replace in title',  inputs: ['find', 'replace'] },
      { value: 'set_vendor',            label: 'Set vendor to',            inputs: ['value'] },
      { value: 'set_product_type',      label: 'Set product type to',      inputs: ['value'] },
      { value: 'add_tag',               label: 'Add tag',                  inputs: ['value'] },
      { value: 'remove_tag',            label: 'Remove tag',               inputs: ['value'] },
      { value: 'set_tags',              label: 'Replace all tags with',    inputs: ['value'] },
      { value: 'set_status',            label: 'Set status to',            inputs: ['status'] },
      { value: 'set_body_html',         label: 'Set description to',       inputs: ['textarea'] },
      { value: 'find_replace_body_html',label: 'Find & replace in description', inputs: ['find', 'replace'] },
      { value: 'convert_description_to_paragraphs', label: 'Convert description to paragraph text', inputs: [] },
      { value: 'remove_description_between', label: 'Remove from description between phrases', inputs: ['remove_between'] },
    ],
  },
  {
    group: 'Variant / Pricing',
    rules: [
      { value: 'set_price',            label: 'Set price to $',           inputs: ['price'] },
      { value: 'increase_price_pct',   label: 'Increase price by %',      inputs: ['pct'] },
      { value: 'decrease_price_pct',   label: 'Decrease price by %',      inputs: ['pct'] },
      { value: 'increase_price_amt',   label: 'Increase price by $',      inputs: ['price'] },
      { value: 'decrease_price_amt',   label: 'Decrease price by $',      inputs: ['price'] },
      { value: 'set_compare_at_price',    label: 'Set compare-at price to $',      inputs: ['price'] },
      { value: 'clear_compare_at_price',  label: 'Clear compare-at price',         inputs: [] },
      { value: 'set_price_to_compare_at', label: 'Set price to compare-at price',  inputs: [] },
      { value: 'set_sku',              label: 'Set SKU to',               inputs: ['value'] },
      { value: 'find_replace_sku',     label: 'Find & replace in SKU',    inputs: ['find', 'replace'] },
      { value: 'set_weight',           label: 'Set weight (kg) to',       inputs: ['num'] },
      { value: 'set_taxable',          label: 'Set taxable',              inputs: ['bool'] },
      { value: 'set_inventory_policy', label: 'Set inventory policy',     inputs: ['inv_policy'] },
      { value: 'set_track_quantity',   label: 'Set track quantity',       inputs: ['track_qty'] },
    ],
  },
  {
    group: 'Theme',
    rules: [
      { value: 'set_template', label: 'Set product template', inputs: ['template'] },
    ],
  },
  {
    group: 'Metafields',
    rules: [
      { value: 'set_metafield', label: 'Set metafield', inputs: ['metafield'] },
    ],
  },
  {
    group: 'Inventory Locations',
    rules: [
      { value: 'assign_inventory_location', label: 'Assign inventory location',    inputs: ['location'] },
      { value: 'remove_inventory_location', label: 'Remove inventory location',    inputs: ['location'] },
      { value: 'set_inventory_quantity',    label: 'Set inventory quantity to',    inputs: ['inv_quantity'] },
    ],
  },
];

const ALL_UPDATE_RULES = UPDATE_RULE_GROUPS.flatMap((g) => g.rules);

// ── Shared sub-components ────────────────────────────────────────────────────

function Sel({ value, onChange, options, placeholder, className = '' }: {
  value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string; className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`bg-muted/50 border-border rounded-xl text-sm text-foreground focus:ring-primary/40 focus:border-primary/50 ${className}`}>
        <SelectValue placeholder={placeholder ?? 'Select…'} />
      </SelectTrigger>
      <SelectContent side="bottom" avoidCollisions={false} className="max-h-60 overflow-y-auto">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SearchableSel({ value, onChange, groups, placeholder, className = '' }: {
  value: string;
  onChange: (v: string) => void;
  groups: { group: string; options: { value: string; label: string }[] }[];
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const allOptions = groups.flatMap((g) => g.options);
  const selectedLabel = allOptions.find((o) => o.value === value)?.label ?? placeholder ?? 'Select…';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center justify-between gap-2 bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 hover:border-input transition-all ${className}`}
        >
          <span className="truncate text-left">{value ? selectedLabel : <span className="text-muted-foreground">{placeholder ?? 'Select…'}</span>}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border"
        style={{ width: 'var(--radix-popover-trigger-width)', minWidth: 260 }}
        align="start"
        sideOffset={4}
      >
        <Command className="bg-transparent">
          <CommandInput placeholder="Search…" className="h-9 text-foreground placeholder:text-muted-foreground" />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">No results.</CommandEmpty>
            {groups.map((g) => (
              <CommandGroup key={g.group} heading={g.group} className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                {g.options.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={`${g.group} ${o.label}`}
                    onSelect={() => { onChange(o.value); setOpen(false); }}
                    className="text-sm text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground cursor-pointer"
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${value === o.value ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Inp({ value, onChange, placeholder, type = 'text', className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; className?: string;
}) {
  return (
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className={`bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary/50 ${className}`} />
  );
}

// ── Filter rule row ───────────────────────────────────────────────────────────

function FilterRow({ rule, onChange, onRemove, index }: {
  rule: FilterRule; onChange: (r: FilterRule) => void; onRemove: () => void; index: number;
}) {
  const typeDef = FILTER_TYPE_DEFS.find((d) => d.value === rule.type) ?? FILTER_TYPE_DEFS[0]!;
  const opDef   = typeDef.operators.find((o) => o.value === rule.operator) ?? typeDef.operators[0]!;
  const showOpSel = typeDef.operators.length > 1;

  const handleTypeChange = (t: string) => {
    const td      = FILTER_TYPE_DEFS.find((d) => d.value === t) ?? FILTER_TYPE_DEFS[0]!;
    const firstOp = td.operators[0]!;
    const defVal  = firstOp.valueType === 'select' ? (firstOp.selectOpts?.[0]?.value ?? '') : '';
    onChange({ type: t, operator: firstOp.value, value: defVal });
  };

  const handleOpChange = (op: string) => {
    const newOp = typeDef.operators.find((o) => o.value === op) ?? typeDef.operators[0]!;
    const val   = newOp.valueType === 'none'   ? undefined
                : newOp.valueType === 'select' ? (newOp.selectOpts?.[0]?.value ?? '')
                : rule.value ?? '';
    onChange({ ...rule, operator: op, value: val });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {index > 0 && <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded">AND</span>}

      {/* Field selector — grouped */}
      <Select value={rule.type} onValueChange={handleTypeChange}>
        <SelectTrigger className="flex-shrink-0 w-56 bg-muted/50 border-border rounded-xl text-sm text-foreground focus:ring-primary/40 focus:border-primary/50">
          <SelectValue />
        </SelectTrigger>
        <SelectContent side="bottom" avoidCollisions={false} className="max-h-60 overflow-y-auto">
          {FILTER_GROUPS.map((g) => (
            <SelectGroup key={g}>
              <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">{g}</SelectLabel>
              {GROUPED_FILTERS[g]!.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

      {/* Operator selector (hidden when there's only one option) */}
      {showOpSel && (
        <Sel value={rule.operator} onChange={handleOpChange}
          options={typeDef.operators.map((o) => ({ value: o.value, label: o.label }))}
          className="flex-shrink-0 w-48" />
      )}

      {/* Value input — type driven by selected operator */}
      {opDef.valueType === 'text' && (
        <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          placeholder={['contains', 'not_contains'].includes(rule.operator) ? 'Word 1, Word 2, …' : 'Value…'}
          className="flex-1 min-w-32" />
      )}
      {opDef.valueType === 'number' && (
        <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          type="number" placeholder="0" className="w-32" />
      )}
      {opDef.valueType === 'date' && (
        <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          type="date" className="w-40" />
      )}
      {opDef.valueType === 'select' && (
        <Sel value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          options={opDef.selectOpts ?? []} className="flex-shrink-0 w-48" />
      )}
      {opDef.valueType === 'id_list' && (
        <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          placeholder="123, 456, 789…" className="flex-1 min-w-32" />
      )}

      <button onClick={onRemove}
        className="p-2 text-muted-foreground hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-colors flex-shrink-0">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Update rule row ───────────────────────────────────────────────────────────

function UpdateRow({ rule, onChange, onRemove, templates, templatesLoading, storeLocations, locationsLoading }: {
  rule: UpdateRule; onChange: (r: UpdateRule) => void; onRemove: () => void;
  templates?: { label: string; value: string }[];
  templatesLoading?: boolean;
  storeLocations?: { id: string; name: string; active: boolean }[];
  locationsLoading?: boolean;
}) {
  const cfg = ALL_UPDATE_RULES.find((r) => r.value === rule.type) ?? ALL_UPDATE_RULES[0]!;
  const ruleGroups = UPDATE_RULE_GROUPS.map((g) => ({
    group: g.group,
    options: g.rules.map((r) => ({ value: r.value, label: r.label })),
  }));

  return (
    <div className="flex items-start gap-2 flex-wrap">
      <SearchableSel
        value={rule.type}
        onChange={(t) => onChange({ type: t as UpdateRuleType })}
        groups={ruleGroups}
        placeholder="Select action…"
        className="w-64 flex-shrink-0"
      />
      {cfg.inputs.includes('value') && (
        <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          placeholder="Value…" className="flex-1 min-w-32" />
      )}
      {cfg.inputs.includes('textarea') && (
        <textarea value={rule.value ?? ''} onChange={(e) => onChange({ ...rule, value: e.target.value })}
          placeholder="Description HTML…" rows={3}
          className="flex-1 min-w-40 bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary/50 resize-none" />
      )}
      {cfg.inputs.includes('status') && (
        <Sel value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          options={[{ value: 'active', label: 'Active' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]}
          placeholder="Status…" className="w-36" />
      )}
      {cfg.inputs.includes('bool') && (
        <Sel value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          options={[{ value: 'true', label: 'Yes (taxable)' }, { value: 'false', label: 'No (not taxable)' }]}
          placeholder="Select…" className="w-44" />
      )}
      {cfg.inputs.includes('inv_policy') && (
        <Sel value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          options={[{ value: 'deny', label: 'Deny (stop selling when out)' }, { value: 'continue', label: 'Continue (sell when out)' }]}
          placeholder="Select policy…" className="w-56" />
      )}
      {cfg.inputs.includes('track_qty') && (
        <Sel value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          options={[{ value: 'enable', label: 'Enable (track quantity)' }, { value: 'disable', label: 'Disable (do not track)' }]}
          placeholder="Select…" className="w-56" />
      )}
      {cfg.inputs.includes('inv_quantity') && (
        locationsLoading ? (
          <span className="text-xs text-muted-foreground px-2 py-2 flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading locations…
          </span>
        ) : storeLocations && storeLocations.length > 0 ? (
          <>
            <Sel
              value={rule.locationId ?? ''}
              onChange={(v) => onChange({ ...rule, locationId: v })}
              options={storeLocations.map((l) => ({ value: l.id, label: l.name + (!l.active ? ' (inactive)' : '') }))}
              placeholder="Select location…"
              className="w-56 flex-shrink-0"
            />
            <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
              type="number" placeholder="0" className="w-28 flex-shrink-0" />
          </>
        ) : (
          <span className="text-xs text-amber-400 px-2 py-2">
            No locations loaded — select a store first.
          </span>
        )
      )}
      {cfg.inputs.includes('template') && (
        templatesLoading ? (
          <span className="text-xs text-muted-foreground px-2 py-2 flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading templates…
          </span>
        ) : (
          <Sel
            value={rule.value === '' || rule.value == null ? '__default__' : rule.value}
            onChange={(v) => onChange({ ...rule, value: v === '__default__' ? '' : v })}
            options={[{ value: '__default__', label: 'Default template' }, ...(templates ?? [])]}
            placeholder="Select template…"
            className="w-64 flex-shrink-0"
          />
        )
      )}
      {cfg.inputs.includes('location') && (
        locationsLoading ? (
          <span className="text-xs text-muted-foreground px-2 py-2 flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading locations…
          </span>
        ) : storeLocations && storeLocations.length > 0 ? (
          <Sel
            value={rule.value ?? ''}
            onChange={(v) => onChange({ ...rule, value: v })}
            options={storeLocations.map((l) => ({ value: l.id, label: l.name + (!l.active ? ' (inactive)' : '') }))}
            placeholder="Select location…"
            className="w-64 flex-shrink-0"
          />
        ) : (
          <span className="text-xs text-amber-400 px-2 py-2">
            No locations loaded — select a store first.
          </span>
        )
      )}
      {(cfg.inputs.includes('price') || cfg.inputs.includes('pct') || cfg.inputs.includes('num')) && (
        <Inp value={rule.value ?? ''} onChange={(v) => onChange({ ...rule, value: v })}
          type="number" placeholder={cfg.inputs.includes('pct') ? '10' : '0.00'} className="w-32" />
      )}
      {cfg.inputs.includes('find') && (
        <>
          <Inp value={rule.find ?? ''} onChange={(v) => onChange({ ...rule, find: v })}
            placeholder="Find…" className="flex-1 min-w-28" />
          <Inp value={rule.replace ?? ''} onChange={(v) => onChange({ ...rule, replace: v })}
            placeholder="Replace with…" className="flex-1 min-w-28" />
        </>
      )}
      {cfg.inputs.includes('remove_between') && (
        <>
          <div className="flex flex-col gap-1 flex-1 min-w-36">
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest px-1">From phrase (start)</span>
            <Inp value={rule.find ?? ''} onChange={(v) => onChange({ ...rule, find: v })}
              placeholder="e.g. <div class=&quot;specs&quot;>" className="w-full" />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-36">
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest px-1">To phrase (end, inclusive)</span>
            <Inp value={rule.replace ?? ''} onChange={(v) => onChange({ ...rule, replace: v })}
              placeholder="e.g. </div>" className="w-full" />
          </div>
        </>
      )}
      {cfg.inputs.includes('metafield') && (
        <div className="flex items-center gap-2 flex-wrap flex-1">
          <Inp
            value={rule.namespace ?? 'custom'}
            onChange={(v) => onChange({ ...rule, namespace: v })}
            placeholder="namespace"
            className="w-28 flex-shrink-0"
          />
          <span className="text-muted-foreground text-sm flex-shrink-0">.</span>
          <Inp
            value={rule.metaKey ?? ''}
            onChange={(v) => onChange({ ...rule, metaKey: v })}
            placeholder="key"
            className="w-28 flex-shrink-0"
          />
          <Sel
            value={rule.metaType ?? 'single_line_text_field'}
            onChange={(v) => onChange({ ...rule, metaType: v })}
            options={[
              { value: 'single_line_text_field', label: 'Single line text' },
              { value: 'multi_line_text_field',  label: 'Multi-line text' },
              { value: 'integer',                label: 'Integer' },
              { value: 'number_decimal',         label: 'Decimal number' },
              { value: 'boolean',                label: 'Boolean (true/false)' },
              { value: 'json',                   label: 'JSON' },
              { value: 'url',                    label: 'URL' },
              { value: 'color',                  label: 'Color' },
              { value: 'date',                   label: 'Date (YYYY-MM-DD)' },
              { value: 'date_time',              label: 'Date & time (ISO)' },
            ]}
            placeholder="Type…"
            className="w-44 flex-shrink-0"
          />
          {(rule.metaType === 'multi_line_text_field' || rule.metaType === 'json') ? (
            <textarea
              value={rule.value ?? ''}
              onChange={(e) => onChange({ ...rule, value: e.target.value })}
              placeholder={rule.metaType === 'json' ? '{"key":"value"}' : 'Value…'}
              rows={2}
              className="flex-1 min-w-48 bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary/50 resize-none"
            />
          ) : rule.metaType === 'boolean' ? (
            <Sel
              value={rule.value ?? 'true'}
              onChange={(v) => onChange({ ...rule, value: v })}
              options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]}
              className="w-28 flex-shrink-0"
            />
          ) : (
            <Inp
              value={rule.value ?? ''}
              onChange={(v) => onChange({ ...rule, value: v })}
              placeholder={
                rule.metaType === 'integer' || rule.metaType === 'number_decimal' ? '0' :
                rule.metaType === 'url' ? 'https://…' :
                rule.metaType === 'color' ? '#FF0000' :
                rule.metaType === 'date' ? 'YYYY-MM-DD' :
                rule.metaType === 'date_time' ? '2025-01-01T00:00:00Z' :
                'Value…'
              }
              className="flex-1 min-w-36"
            />
          )}
        </div>
      )}
      <button onClick={onRemove} className="p-2 text-muted-foreground hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-colors flex-shrink-0 mt-0.5">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Preview diff table ────────────────────────────────────────────────────────

function PreviewTable({ result }: { result: PreviewResult }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <span className="text-muted-foreground">Scanned: <strong className="text-foreground">{result.totalScanned}</strong></span>
          <span className="text-muted-foreground">Will change: <strong className="text-primary">{result.totalMatched}</strong></span>
        </div>
      </div>

      {result.items.length === 0 && (
        <div className="glass-panel rounded-2xl p-8 text-center text-muted-foreground">
          No products match the current filters or all matching products already have the target values.
        </div>
      )}

      <div className="space-y-2 max-h-[480px] overflow-y-auto custom-scrollbar pr-1">
        {result.items.map((item) => (
          <div key={item.productId} className="glass-panel rounded-2xl p-4 flex gap-3">
            {item.image ? (
              <img src={item.image} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-border" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-muted/50 border border-border flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-slate-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                  item.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' :
                  item.status === 'draft' ? 'bg-slate-500/20 text-muted-foreground' : 'bg-amber-500/15 text-amber-400'
                }`}>{item.status}</span>
              </div>
              <div className="space-y-1">
                {item.changes.map((ch, ci) => (
                  <div key={ci} className="flex items-start gap-2 text-xs">
                    <span className="text-muted-foreground w-28 flex-shrink-0 pt-0.5">{ch.field}</span>
                    <span className="text-rose-400 line-through opacity-80 flex-1 min-w-0 truncate">{ch.oldValue || '(empty)'}</span>
                    <span className="text-muted-foreground flex-shrink-0">→</span>
                    <span className="text-emerald-400 flex-1 min-w-0 truncate">{ch.newValue || '(empty)'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Run progress panel ────────────────────────────────────────────────────────

interface RunProduct { productId: number; title: string; success: boolean; error?: string; changes?: FieldChange[] }

function RunPanel({ events, done, progress, summary }: {
  events: RunProduct[]; done: boolean;
  progress: RunProgress | null;
  summary: { totalScanned: number; totalUpdated: number; totalSkipped: number; totalErrors: number; message: string } | null;
}) {
  const pct = progress && progress.total > 0
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      {!done && progress && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              Processing products…
            </span>
            <span className="font-mono text-foreground">{progress.processed} / {progress.total}</span>
          </div>
          <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>Updated: <strong className="text-emerald-400">{progress.totalUpdated}</strong></span>
            <span>Skipped: <strong className="text-foreground">{progress.totalSkipped}</strong></span>
            {progress.totalErrors > 0 && <span>Errors: <strong className="text-rose-400">{progress.totalErrors}</strong></span>}
          </div>
        </div>
      )}

      {/* Fetching state (before first progress event) */}
      {!done && !progress && events.length === 0 && (
        <div className="flex items-center gap-3 text-muted-foreground py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span className="text-sm">Fetching products from Shopify…</span>
        </div>
      )}

      {/* Final summary */}
      {summary && (
        <div className={`rounded-2xl p-4 ${summary.totalErrors > 0 && summary.totalUpdated === 0 ? 'bg-rose-500/10 border border-rose-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
          <p className="text-sm font-medium text-foreground mb-2">{summary.message}</p>
          <div className="flex gap-4 text-xs">
            <span className="text-muted-foreground">Scanned: <strong className="text-foreground">{summary.totalScanned}</strong></span>
            <span className="text-muted-foreground">Updated: <strong className="text-emerald-400">{summary.totalUpdated}</strong></span>
            <span className="text-muted-foreground">Skipped: <strong className="text-foreground">{summary.totalSkipped}</strong></span>
            {summary.totalErrors > 0 && <span className="text-muted-foreground">Errors: <strong className="text-rose-400">{summary.totalErrors}</strong></span>}
          </div>
        </div>
      )}

      {/* Per-product events */}
      <div className="space-y-2 max-h-[360px] overflow-y-auto custom-scrollbar pr-1">
        {events.map((ev, i) => (
          <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${ev.success ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-rose-500/5 border-rose-500/15'}`}>
            {ev.success
              ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              : <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{ev.title}</p>
              {ev.success && ev.changes && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ev.changes.map((ch, ci) => (
                    <span key={ci} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{ch.field}</span>
                  ))}
                </div>
              )}
              {!ev.success && ev.error && (
                <p className="text-xs text-rose-400 mt-0.5 truncate">{ev.error}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Manage Location Section ───────────────────────────────────────────────────

function RemoveLocationSection({ token, storeId, storeUrl, storeLocations, locationsLoading }: {
  token: string | null;
  storeId: string;
  storeUrl: string;
  storeLocations: { id: string; name: string; active: boolean }[];
  locationsLoading: boolean;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<'assign' | 'remove'>('remove');
  const [locationId, setLocationId] = useState('');
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [products, setProducts] = useState<LocationProduct[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set<number>());
  const [vendorFilter, setVendorFilter] = useState(new Set<string>());
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [events, setEvents] = useState<RemoveProductEvent[]>([]);
  const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
  const [summary, setSummary] = useState<{ totalSuccess: number; totalErrors: number; message: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleModeChange = (m: 'assign' | 'remove') => {
    setMode(m);
    setLoaded(false);
    setProducts([]);
    setSelectedIds(new Set());
    setVendorFilter(new Set());
    setDone(false);
    setEvents([]);
    setSummary(null);
    setProgress(null);
  };

  // Background product counts per location
  const [locationCounts, setLocationCounts] = useState<Record<string, number>>({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsLastUpdated, setCountsLastUpdated] = useState<number | null>(null);

  const fetchLocationCounts = React.useCallback((force = false) => {
    if (!token || !storeId) { setLocationCounts({}); return () => {}; }
    let cancelled = false;
    setCountsLoading(true);
    const url = `${BASE}/api/product-updates/location-product-counts?storeId=${storeId}${force ? '&force=true' : ''}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : Promise.resolve({ counts: {} }))
      .then((data) => {
        if (!cancelled) {
          setLocationCounts(data.counts ?? {});
          setCountsLastUpdated(data.cachedAt ?? Date.now());
        }
      })
      .catch(() => { if (!cancelled) setLocationCounts({}); })
      .finally(() => { if (!cancelled) setCountsLoading(false); });
    return () => { cancelled = true; };
  }, [token, storeId]);

  React.useEffect(() => {
    const cancel = fetchLocationCounts(false);
    return cancel;
  }, [fetchLocationCounts]);

  // Reset loaded state when location changes
  const handleLocationChange = (id: string) => {
    setLocationId(id);
    setLoaded(false);
    setProducts([]);
    setSelectedIds(new Set());
    setVendorFilter(new Set());
    setDone(false);
    setEvents([]);
    setSummary(null);
    setProgress(null);
  };

  const loadProducts = async () => {
    if (!storeId || !locationId) return;
    setLoadingVariants(true);
    setLoaded(false);
    setProducts([]);
    setSelectedIds(new Set());
    setVendorFilter(new Set());
    try {
      const endpoint = mode === 'assign'
        ? `${BASE}/api/product-updates/location-unassigned-variants?storeId=${storeId}&locationId=${locationId}`
        : `${BASE}/api/product-updates/location-variants?storeId=${storeId}&locationId=${locationId}`;
      const r = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? r.statusText);
      const list: LocationProduct[] = data.products ?? [];
      setProducts(list);
      setSelectedIds(new Set(list.map((p) => p.id)));
      setLoaded(true);
    } catch (err: any) {
      toast({ title: 'Failed to load products', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingVariants(false);
    }
  };

  // Derived: unique sorted vendors from loaded products
  const allVendors = React.useMemo(
    () => [...new Set(products.map((p) => p.vendor).filter(Boolean))].sort(),
    [products],
  );

  // Derived: products visible after vendor filter
  const filteredProducts = React.useMemo(
    () => vendorFilter.size === 0 ? products : products.filter((p) => vendorFilter.has(p.vendor)),
    [products, vendorFilter],
  );

  const toggleVendor = (v: string) => {
    setVendorFilter((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const toggleAll = () => {
    const filteredIds = filteredProducts.map((p) => p.id);
    const allFilteredSelected = filteredIds.every((id) => selectedIds.has(id));
    if (allFilteredSelected) {
      setSelectedIds((prev) => { const next = new Set(prev); filteredIds.forEach((id) => next.delete(id)); return next; });
    } else {
      setSelectedIds((prev) => { const next = new Set(prev); filteredIds.forEach((id) => next.add(id)); return next; });
    }
  };

  const toggleProduct = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleRun = async () => {
    if (!storeId || !locationId || selectedIds.size === 0) return;
    // Only act on products that are both selected AND currently visible (filtered)
    const items = filteredProducts
      .filter((p) => selectedIds.has(p.id))
      .map((p) => ({
        productId: p.id,
        productTitle: p.title,
        inventoryItemIds: p.variants.map((v) => v.inventoryItemId),
      }));

    setRunning(true);
    setDone(false);
    setEvents([]);
    setProgress(null);
    setSummary(null);
    abortRef.current = new AbortController();

    const endpoint = mode === 'assign'
      ? `${BASE}/api/product-updates/assign-location`
      : `${BASE}/api/product-updates/remove-location`;

    try {
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ storeId, locationId, items }),
        signal: abortRef.current.signal,
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? r.statusText);
      }

      const reader = r.body!.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';

        for (const chunk of parts) {
          const eventLine = chunk.match(/^event:\s*(.+)$/m)?.[1]?.trim();
          const dataLine = chunk.match(/^data:\s*(.+)$/m)?.[1]?.trim();
          if (!dataLine) continue;
          try {
            const payload: any = JSON.parse(dataLine);
            if (eventLine === 'product') {
              setProgress({ processed: payload.processed, total: payload.total });
              setEvents((prev) => [...prev, payload as RemoveProductEvent]);
            } else if (eventLine === 'done') {
              setSummary({ totalSuccess: payload.totalSuccess, totalErrors: payload.totalErrors, message: payload.message });
              setDone(true);
              // Refresh counts after the operation so the picker reflects new totals
              fetchLocationCounts(true);
            } else if (eventLine === 'error') {
              toast({ title: 'Error', description: payload.message, variant: 'destructive' });
              setDone(true);
            }
          } catch {}
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        toast({ title: 'Remove failed', description: err.message, variant: 'destructive' });
      }
      setDone(true);
    } finally {
      setRunning(false);
    }
  };

  const handleReset = () => {
    abortRef.current?.abort();
    setDone(false);
    setRunning(false);
    setEvents([]);
    setSummary(null);
    setProgress(null);
    setLoaded(false);
    setProducts([]);
    setSelectedIds(new Set());
    setVendorFilter(new Set());
  };

  const locationName = storeLocations.find((l) => l.id === locationId)?.name ?? locationId;
  const pct = progress && progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div className="space-y-6">

      {/* Mode toggle */}
      <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-xl w-fit">
        {([
          { key: 'assign', label: 'Assign Location' },
          { key: 'remove', label: 'Remove Location' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleModeChange(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mode === key
                ? 'bg-primary/15 text-primary border border-primary/20'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Location picker */}
      <div className="glass-panel rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-primary" />
          <h2 className="font-semibold text-foreground">
            {mode === 'assign' ? 'Location to Assign' : 'Location to Remove'}
          </h2>
        </div>
        {locationsLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading locations…
          </div>
        ) : storeLocations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No locations found — select a store first.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Sel
                  value={locationId}
                  onChange={handleLocationChange}
                  options={storeLocations.map((l) => {
                    const count = locationCounts[l.id];
                    const isSafeToDelete = !countsLoading && count === 0;
                    const countStr = count != null
                      ? isSafeToDelete
                        ? ' — 0 products · safe to delete'
                        : ` — ${count} product${count !== 1 ? 's' : ''}`
                      : '';
                    return {
                      value: l.id,
                      label: l.name + (!l.active ? ' (inactive)' : '') + countStr,
                    };
                  })}
                  placeholder="Select location…"
                  className="max-w-xs"
                />
                {countsLoading ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" /> Refreshing counts…
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    {countsLastUpdated != null && (
                      <span className="text-xs text-muted-foreground">
                        Counts updated {Math.round((Date.now() - countsLastUpdated) / 1000) < 60
                          ? `${Math.round((Date.now() - countsLastUpdated) / 1000)}s ago`
                          : `${Math.round((Date.now() - countsLastUpdated) / 60000)}m ago`}
                      </span>
                    )}
                    <button
                      onClick={() => fetchLocationCounts(true)}
                      disabled={countsLoading}
                      title="Refresh location product counts"
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent hover:border-border transition-colors disabled:opacity-40"
                    >
                      <RefreshCw className="w-3 h-3" /> Refresh counts
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={loadProducts}
                disabled={!locationId || loadingVariants || running}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loadingVariants ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {loadingVariants ? 'Loading…' : 'Load products'}
              </button>
            </div>

            {/* Safe-to-delete callout + admin link — shown once counts have loaded and a location is selected */}
            {!countsLoading && locationId && (() => {
              const count = locationCounts[locationId];
              const adminBase = storeUrl
                ? (storeUrl.startsWith('http') ? storeUrl : `https://${storeUrl}`)
                : null;
              const adminUrl = adminBase ? `${adminBase}/admin/settings/locations/${locationId}` : null;

              return (
                <div className="flex items-center gap-3 flex-wrap">
                  {count === 0 && (
                    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                      0 products — safe to delete in Shopify
                    </span>
                  )}
                  {adminUrl && (
                    <a
                      href={adminUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 hover:underline transition-colors"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      Open in Shopify Admin ↗
                    </a>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Product table */}
      {loaded && !running && !done && (
        <div className="glass-panel rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-foreground">
                {mode === 'assign' ? `Products not yet at "${locationName}"` : `Products at "${locationName}"`}
              </h2>
              <span className="text-xs text-muted-foreground">
                {filteredProducts.length}{filteredProducts.length !== products.length ? ` of ${products.length}` : ''} product{products.length !== 1 ? 's' : ''}
              </span>
            </div>
            {filteredProducts.length > 0 && (
              <button onClick={toggleAll} className="text-xs text-primary hover:underline">
                {filteredProducts.every((p) => selectedIds.has(p.id)) ? 'Deselect all' : 'Select all'}
              </button>
            )}
          </div>

          {/* Vendor filter pills */}
          {allVendors.length > 1 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                  <Filter className="w-3 h-3" /> Vendor:
                </span>
                <button
                  onClick={() => setVendorFilter(new Set())}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    vendorFilter.size === 0
                      ? 'bg-primary/15 border-primary/30 text-primary'
                      : 'border-border text-muted-foreground hover:border-input hover:text-foreground'
                  }`}
                >
                  All
                </button>
                {allVendors.map((v) => (
                  <button
                    key={v}
                    onClick={() => toggleVendor(v)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      vendorFilter.has(v)
                        ? 'bg-primary/15 border-primary/30 text-primary'
                        : 'border-border text-muted-foreground hover:border-input hover:text-foreground'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {products.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">
                {mode === 'assign'
                  ? 'All tracked products are already assigned to this location.'
                  : 'No products are stocked at this location.'}
              </p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Filter className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No products match the selected vendor filter.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-[420px] overflow-y-auto custom-scrollbar pr-1 mb-4">
                {filteredProducts.map((p) => {
                  const selected = selectedIds.has(p.id);
                  return (
                    <div
                      key={p.id}
                      onClick={() => toggleProduct(p.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        selected
                          ? 'bg-primary/5 border-primary/20'
                          : 'bg-muted/20 border-border hover:border-input'
                      }`}
                    >
                      {/* Checkbox */}
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                        selected ? 'bg-primary border-primary' : 'border-border'
                      }`}>
                        {selected && <Check className="w-2.5 h-2.5 text-black" />}
                      </div>

                      {/* Image */}
                      {p.image ? (
                        <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted/50 border border-border flex items-center justify-center flex-shrink-0">
                          <Package className="w-4 h-4 text-slate-600" />
                        </div>
                      )}

                      {/* Title + vendor + variants */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{p.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.vendor && <span className="text-muted-foreground">{p.vendor} · </span>}
                          {p.variants.length} variant{p.variants.length !== 1 ? 's' : ''}
                          {p.variants.length <= 3 && p.variants.some((v) => v.sku) && (
                            <> · {p.variants.map((v) => v.sku).filter(Boolean).join(', ')}</>
                          )}
                        </p>
                      </div>

                      {/* Qty badge */}
                      <div className="flex-shrink-0 text-right">
                        <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${
                          p.totalQty > 0
                            ? 'bg-amber-500/15 text-amber-400'
                            : 'bg-muted/50 text-muted-foreground'
                        }`}>
                          {p.totalQty > 0 ? `${p.totalQty} in stock` : 'OOS'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Warning + action */}
              {(() => {
                const selCount = filteredProducts.filter((p) => selectedIds.has(p.id)).length;
                const withStock = filteredProducts.filter((p) => selectedIds.has(p.id) && p.totalQty > 0).length;
                return selCount > 0 ? (
                  <div className="border-t border-border pt-4 space-y-3">
                    {mode === 'remove' && withStock > 0 && (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          {withStock} selected product{withStock !== 1 ? 's have' : ' has'} stock at this location — inventory will be zeroed automatically before the location is removed.
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {selCount} of {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} selected
                        {vendorFilter.size > 0 && <span className="text-muted-foreground"> (filtered)</span>}
                      </span>
                      {mode === 'assign' ? (
                        <button
                          onClick={handleRun}
                          className="flex items-center gap-2 px-5 py-2 rounded-xl font-semibold text-sm bg-emerald-500 text-white hover:bg-emerald-600 transition-all"
                        >
                          <MapPin className="w-4 h-4" />
                          Assign to {selCount} product{selCount !== 1 ? 's' : ''}
                        </button>
                      ) : (
                        <button
                          onClick={handleRun}
                          className="flex items-center gap-2 px-5 py-2 rounded-xl font-semibold text-sm bg-rose-500 text-white hover:bg-rose-600 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove from {selCount} product{selCount !== 1 ? 's' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                ) : null;
              })()}
            </>
          )}
        </div>
      )}

      {/* Progress / results */}
      {(running || done) && (
        <div className="glass-panel rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {running && !done
                ? <Loader2 className="w-5 h-5 text-primary animate-spin" />
                : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
              <h2 className="font-semibold text-foreground">
                {running && !done
                  ? (mode === 'assign' ? 'Assigning location…' : 'Removing location…')
                  : 'Complete'}
              </h2>
            </div>
            {done && (
              <button onClick={handleReset} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                <RefreshCw className="w-4 h-4" /> Start over
              </button>
            )}
          </div>

          {/* Progress bar */}
          {!done && progress && (
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin text-primary" /> Processing…</span>
                <span className="font-mono text-foreground">{progress.processed} / {progress.total}</span>
              </div>
              <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {/* Summary */}
          {summary && (
            <div className={`rounded-2xl p-4 mb-4 ${summary.totalErrors > 0 && summary.totalSuccess === 0 ? 'bg-rose-500/10 border border-rose-500/20' : 'bg-emerald-500/10 border border-emerald-500/20'}`}>
              <p className="text-sm font-medium text-foreground">{summary.message}</p>
              <div className="flex gap-4 text-xs mt-1">
                <span className="text-muted-foreground">{mode === 'assign' ? 'Assigned:' : 'Removed:'} <strong className="text-emerald-400">{summary.totalSuccess}</strong></span>
                {summary.totalErrors > 0 && <span className="text-muted-foreground">Errors: <strong className="text-rose-400">{summary.totalErrors}</strong></span>}
              </div>
            </div>
          )}

          {/* Per-product events */}
          <div className="space-y-2 max-h-[360px] overflow-y-auto custom-scrollbar pr-1">
            {events.map((ev, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${ev.success ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-rose-500/5 border-rose-500/15'}`}>
                {ev.success
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  : <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{ev.title}</p>
                  {!ev.success && ev.error && (
                    <p className="text-xs text-rose-400 mt-0.5">{ev.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Step = 'configure' | 'preview' | 'run';

export function ProductUpdates() {
  const { token } = useAuthStore();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<'updater' | 'remove-location'>('updater');

  const [step, setStep] = useState<Step>('configure');
  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
  const [updateRules, setUpdateRules] = useState<UpdateRule[]>([{ type: 'set_price', value: '' }]);

  const [templates, setTemplates] = useState<{ label: string; value: string }[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [storeLocations, setStoreLocations] = useState<{ id: string; name: string; active: boolean }[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);

  const [runEvents, setRunEvents] = useState<RunProduct[]>([]);
  const [runDone, setRunDone] = useState(false);
  const [runProgress, setRunProgress] = useState<RunProgress | null>(null);
  const [runSummary, setRunSummary] = useState<{ totalScanned: number; totalUpdated: number; totalSkipped: number; totalErrors: number; message: string } | null>(null);
  const [running, setRunning] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // Load stores on mount
  React.useEffect(() => {
    if (!token) return;
    setStoresLoading(true);
    fetch(`${BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        const list: ShopifyStore[] = Array.isArray(data) ? data : [];
        setStores(list);
        if (list.length > 0) setSelectedStoreId(list[0]!.id);
      })
      .catch(() => {})
      .finally(() => setStoresLoading(false));
  }, [token]);

  // Fetch store locations when the selected store changes
  React.useEffect(() => {
    if (!token || !selectedStoreId) { setStoreLocations([]); return; }
    let cancelled = false;
    setLocationsLoading(true);
    fetch(`${BASE}/api/product-updates/locations?storeId=${selectedStoreId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : Promise.resolve({ locations: [] }))
      .then((data) => { if (!cancelled) setStoreLocations(data.locations ?? []); })
      .catch(() => { if (!cancelled) setStoreLocations([]); })
      .finally(() => { if (!cancelled) setLocationsLoading(false); });
    return () => { cancelled = true; };
  }, [token, selectedStoreId]);

  // Fetch product templates from the selected store's published theme
  React.useEffect(() => {
    if (!token || !selectedStoreId) { setTemplates([]); return; }
    let cancelled = false;
    setTemplatesLoading(true);
    fetch(`${BASE}/api/wheelpros/product-templates?storeId=${selectedStoreId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : Promise.resolve({ templates: [] }))
      // Filter out any empty-string values — Radix UI Select crashes on value=""
      .then((data) => { if (!cancelled) setTemplates((data.templates ?? []).filter((t: { value: string }) => t.value !== '')); })
      .catch(() => { if (!cancelled) setTemplates([]); })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [token, selectedStoreId]);

  // Filter rule helpers
  const addFilterRule = () => {
    const td = FILTER_TYPE_DEFS[0]!;
    const op = td.operators[0]!;
    setFilterRules((prev) => [...prev, { type: td.value, operator: op.value, value: '' }]);
  };
  const updateFilter = (i: number, r: FilterRule) => setFilterRules((prev) => prev.map((x, j) => j === i ? r : x));
  const removeFilter = (i: number) => setFilterRules((prev) => prev.filter((_, j) => j !== i));

  // Update rule helpers
  const addUpdateRule = () => setUpdateRules((prev) => [...prev, { type: 'set_price', value: '' }]);
  const updateRule = (i: number, r: UpdateRule) => setUpdateRules((prev) => prev.map((x, j) => j === i ? r : x));
  const removeUpdateRule = (i: number) => setUpdateRules((prev) => prev.filter((_, j) => j !== i));

  // Preview
  const handlePreview = useCallback(async () => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    if (updateRules.length === 0) { toast({ title: 'Add at least one update rule', variant: 'destructive' }); return; }
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const r = await fetch(`${BASE}/api/product-updates/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ storeId: selectedStoreId, filterRules, updateRules }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? r.statusText);
      setPreviewResult(data as PreviewResult);
      setStep('preview');
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  }, [selectedStoreId, filterRules, updateRules, token, toast]);

  // Run (SSE) — with auto-reconnect so a proxy timeout won't stop the job
  const handleRun = useCallback(async () => {
    if (!previewResult || previewResult.totalMatched === 0) {
      toast({ title: 'Nothing to update', variant: 'destructive' }); return;
    }
    setRunEvents([]);
    setRunDone(false);
    setRunProgress(null);
    setRunSummary(null);
    setRunning(true);
    setStep('run');

    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    // Persistent state across reconnects
    let jobId: string | null = null;
    let eventsReceived = 0; // how many buffered events we've already seen
    let finished = false;

    // Reads an SSE stream (fetch Response) and applies events to state.
    // Returns true when "done"/"error" is received, false when the stream
    // closes early (proxy timeout) so the caller can reconnect.
    const drainStream = async (response: Response): Promise<boolean> => {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';

        for (const chunk of parts) {
          const eventLine = chunk.match(/^event:\s*(.+)$/m)?.[1]?.trim();
          const dataLine  = chunk.match(/^data:\s*(.+)$/m)?.[1]?.trim();
          if (!dataLine) continue;
          try {
            const payload: any = JSON.parse(dataLine);
            eventsReceived++;
            if (eventLine === 'jobId') {
              jobId = payload.jobId;
            } else if (eventLine === 'progress') {
              setRunProgress({
                processed:    payload.processed    ?? 0,
                total:        payload.total        ?? 0,
                totalUpdated: payload.totalUpdated ?? 0,
                totalSkipped: payload.totalSkipped ?? 0,
                totalErrors:  payload.totalErrors  ?? 0,
              });
            } else if (eventLine === 'product') {
              setRunEvents((prev) => [...prev, {
                productId: payload.productId,
                title:     payload.title,
                success:   payload.success,
                error:     payload.error,
                changes:   payload.changes,
              }]);
            } else if (eventLine === 'done') {
              setRunSummary({
                totalScanned: payload.totalScanned ?? 0,
                totalUpdated: payload.totalUpdated ?? 0,
                totalSkipped: payload.totalSkipped ?? 0,
                totalErrors:  payload.totalErrors  ?? 0,
                message:      payload.message      ?? 'Done.',
              });
              setRunDone(true);
              finished = true;
              return true;
            } else if (eventLine === 'error') {
              toast({ title: 'Run error', description: payload.message, variant: 'destructive' });
              setRunDone(true);
              finished = true;
              return true;
            }
          } catch {}
        }
      }
      return false; // stream closed without done — likely proxy timeout
    };

    try {
      // ── Initial request ──────────────────────────────────────────────────
      const r = await fetch(`${BASE}/api/product-updates/run`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ storeId: selectedStoreId, filterRules, updateRules }),
        signal,
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? r.statusText);
      }

      finished = await drainStream(r);

      // ── Auto-reconnect loop ──────────────────────────────────────────────
      // If the proxy killed the connection before "done", reconnect to the
      // background job using the jobId we received at the start.
      while (!finished && !signal.aborted && jobId) {
        // Brief back-off before reconnecting
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (signal.aborted) break;

        const reconnect = await fetch(
          `${BASE}/api/product-updates/run/${jobId}/stream?from=${eventsReceived}`,
          { headers: { Authorization: `Bearer ${token}` }, signal },
        ).catch(() => null);

        if (!reconnect || !reconnect.ok) {
          // Job may have expired or server restarted — stop silently
          break;
        }

        finished = await drainStream(reconnect);
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        toast({ title: 'Run failed', description: err.message, variant: 'destructive' });
      }
      setRunDone(true);
    } finally {
      setRunning(false);
    }
  }, [previewResult, selectedStoreId, filterRules, updateRules, token, toast]);

  const handleReset = () => {
    abortRef.current?.abort();
    setStep('configure');
    setPreviewResult(null);
    setRunEvents([]);
    setRunDone(false);
    setRunProgress(null);
    setRunSummary(null);
    setRunning(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const selectedStore = stores.find((s) => s.id === selectedStoreId);

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold text-gradient mb-1">Product Tools</h1>
          <p className="text-muted-foreground text-sm">Bulk update products, or assign / remove a location across your inventory.</p>
        </div>
        {activeTab === 'updater' && (step === 'preview' || step === 'run') && (
          <button onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Start over
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-xl mb-8 w-fit">
        {([
          { key: 'updater', label: 'Bulk Updater', icon: Package },
          { key: 'remove-location', label: 'Manage Location', icon: MapPin },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === key
                ? 'bg-primary/15 text-primary border border-primary/20'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Remove Location tab ──────────────────────────────────────────────── */}
      {activeTab === 'remove-location' && (
        <>
          {/* Store selector */}
          <div className="glass-panel rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Store className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-foreground">Store</h2>
            </div>
            {storesLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading stores…</div>
            ) : stores.length === 0 ? (
              <p className="text-sm text-muted-foreground">No connected stores. Connect a Shopify store first.</p>
            ) : (
              <ShopifyTargetStoreSelector
                stores={stores}
                value={selectedStoreId}
                onChange={setSelectedStoreId}
                label="Target Shopify store"
                className="max-w-sm"
              />
            )}
          </div>
          <RemoveLocationSection
            token={token}
            storeId={selectedStoreId}
            storeUrl={selectedStore?.storeUrl ?? ''}
            storeLocations={storeLocations}
            locationsLoading={locationsLoading}
          />
        </>
      )}

      {activeTab === 'updater' && (<>

      {/* Step indicators */}
      <div className="flex items-center gap-3 mb-8">
        {([
          { key: 'configure', label: 'Configure', icon: Settings2 },
          { key: 'preview',   label: 'Preview',   icon: Eye },
          { key: 'run',       label: 'Run',        icon: Play },
        ] as const).map(({ key, label, icon: Icon }, i) => {
          const isActive = step === key;
          const isDone = (step === 'preview' && key === 'configure') || (step === 'run' && (key === 'configure' || key === 'preview'));
          return (
            <React.Fragment key={key}>
              {i > 0 && <div className={`h-px flex-1 ${isDone ? 'bg-primary/40' : 'bg-border'}`} />}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? 'bg-primary/15 text-primary border border-primary/30' :
                isDone ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                <Icon className="w-4 h-4" />
                {label}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Step 1: Configure ──────────────────────────────────────────────── */}
      {step === 'configure' && (
        <div className="space-y-6">
          {/* Store selector */}
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Store className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-foreground">Store</h2>
            </div>
            {storesLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading stores…</div>
            ) : stores.length === 0 ? (
              <p className="text-sm text-muted-foreground">No connected stores. Connect a Shopify store first.</p>
            ) : (
              <ShopifyTargetStoreSelector
                stores={stores}
                value={selectedStoreId}
                onChange={setSelectedStoreId}
                label="Target Shopify store"
                className="max-w-sm"
              />
            )}
          </div>

          {/* Filter rules */}
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-foreground">Product Filters</h2>
                <span className="text-xs text-muted-foreground ml-1">(optional — leave empty to target all products)</span>
              </div>
              <button onClick={addFilterRule}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add filter
              </button>
            </div>
            {filterRules.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">All products in the store will be evaluated.</p>
            ) : (
              <div className="space-y-3">
                {filterRules.map((r, i) => (
                  <FilterRow key={i} rule={r} index={i} onChange={(nr) => updateFilter(i, nr)} onRemove={() => removeFilter(i)} />
                ))}
              </div>
            )}
          </div>

          {/* Update rules */}
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-foreground">Update Rules</h2>
              </div>
              <button onClick={addUpdateRule}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add rule
              </button>
            </div>
            {updateRules.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Add at least one update rule.</p>
            ) : (
              <div className="space-y-3">
                {updateRules.map((r, i) => (
                  <UpdateRow key={i} rule={r} onChange={(nr) => updateRule(i, nr)} onRemove={() => removeUpdateRule(i)}
                    templates={templates} templatesLoading={templatesLoading}
                    storeLocations={storeLocations} locationsLoading={locationsLoading} />
                ))}
              </div>
            )}
          </div>

          {/* Preview button */}
          <div className="flex justify-end">
            <button
              onClick={handlePreview}
              disabled={previewing || !selectedStoreId || updateRules.length === 0}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-primary text-black hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_rgba(0,240,255,0.2)]">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              {previewing ? 'Scanning products…' : 'Preview Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Preview ────────────────────────────────────────────────── */}
      {step === 'preview' && previewResult && (
        <div className="space-y-6">
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-foreground">Change Preview</h2>
                {selectedStore && (
                  <span className="text-xs text-muted-foreground">· {selectedStore.nickname || selectedStore.storeUrl}</span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep('configure')}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={handleRun}
                  disabled={previewResult.totalMatched === 0}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl font-semibold text-sm bg-primary text-black hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_rgba(0,240,255,0.2)]">
                  <Play className="w-4 h-4" />
                  Apply {previewResult.totalMatched} {previewResult.totalMatched === 1 ? 'update' : 'updates'}
                </button>
              </div>
            </div>
            <PreviewTable result={previewResult} />
          </div>
        </div>
      )}

      {/* ── Step 3: Run ────────────────────────────────────────────────────── */}
      {step === 'run' && (
        <div className="space-y-6">
          <div className="glass-panel rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {running && !runDone ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                ) : runDone ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : null}
                <h2 className="font-semibold text-foreground">
                  {running && !runDone ? 'Applying updates…' : 'Update complete'}
                </h2>
                {running && !runDone && (
                  <span className="text-xs text-muted-foreground ml-1 animate-pulse">processing at ~2 products/sec</span>
                )}
              </div>
              {runDone && (
                <button onClick={handleReset}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                  <RefreshCw className="w-4 h-4" /> Run again
                </button>
              )}
            </div>
            <RunPanel events={runEvents} done={runDone} progress={runProgress} summary={runSummary} />
          </div>
        </div>
      )}

      </>)}
    </Layout>
  );
}
